import axios from 'axios';

// Função para detectar automaticamente a URL da API baseado no ambiente
const getApiUrl = () => {
  // Se VITE_API_URL estiver definida, usa ela (prioridade)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Se estiver em desenvolvimento (localhost), usa o proxy do Vite
  if (import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return '/api';
  }

  // Se estiver em produção/homologação, detecta automaticamente pela URL atual
  const hostname = window.location.hostname;

  // Se for haevo-solucoes.com.br ou hml.haevo-solucoes.com.br, usa api.haevo-solucoes.com.br
  if (hostname === 'haevo-solucoes.com.br' || hostname === 'hml.haevo-solucoes.com.br') {
    return 'https://api.haevo-solucoes.com.br';
  }

  // Fallback: tenta construir a URL baseado no domínio atual
  // Se estiver em um subdomínio, tenta usar o subdomínio 'api'
  if (hostname.includes('.')) {
    const parts = hostname.split('.');
    // Se for um subdomínio (ex: hml.haevo-solucoes.com.br), usa api.haevo-solucoes.com.br
    if (parts.length > 2) {
      const domain = parts.slice(-2).join('.');
      return `https://api.${domain}`;
    }
    // Se for domínio direto, usa api. + domínio
    return `https://api.${hostname}`;
  }

  // Último fallback: usa caminho relativo
  return '/api';
};

// Normaliza a URL para garantir que sempre termine com /api
// Se for uma URL completa (http/https), garante que termine com /api
// Se for um caminho relativo, mantém como está
const normalizeApiUrl = (url) => {
  // Se for um caminho relativo (começa com /), retorna como está
  if (url.startsWith('/')) {
    return url;
  }

  // Se for uma URL completa, garante que termine com /api
  // Remove barra final se existir e adiciona /api
  const normalizedUrl = url.replace(/\/$/, '');
  return normalizedUrl.endsWith('/api') ? normalizedUrl : `${normalizedUrl}/api`;
};

const resolvedApiUrl = getApiUrl();
const baseURL = normalizeApiUrl(resolvedApiUrl);

const DEFAULT_TIMEOUT_MS = 120000;
const timeoutFromEnv = Number.parseInt(import.meta.env.VITE_API_TIMEOUT ?? '', 10);
const apiTimeout = Number.isFinite(timeoutFromEnv) && timeoutFromEnv > 0 ? timeoutFromEnv : DEFAULT_TIMEOUT_MS;


// Configuração base do axios
const api = axios.create({
  baseURL,
  timeout: apiTimeout, // Timeout configurável via ambiente (default 120s)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token nas requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Se o token expirou ou é inválido, redirecionar para login
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
      const errorData = error.response?.data || {};

      // Verificar se é realmente um erro de autenticação
      // Erros de autenticação geralmente vêm do middleware auth.js
      const isAuthError =
        errorMessage?.toLowerCase().includes('token') ||
        errorMessage?.toLowerCase().includes('expirado') ||
        errorMessage?.toLowerCase().includes('inválido') ||
        errorMessage?.toLowerCase().includes('revogado') ||
        errorMessage?.toLowerCase().includes('acesso requerido') ||
        errorMessage?.toLowerCase().includes('autenticação') ||
        errorMessage?.toLowerCase().includes('não encontrado ou inativo') ||
        // Se não há mensagem de erro específica, provavelmente é erro de autenticação
        (!errorMessage && error.config?.url?.includes('/auth/'));

      // Só redirecionar se for realmente um erro de autenticação
      // Não redirecionar se for erro de validação ou outro tipo de erro 401
      if (isAuthError) {
        localStorage.removeItem('token');

        // Aguardar um pouco para não conflitar com outras chamadas
        setTimeout(() => {
          window.location.href = '/';
        }, 100);
      }
    }

    return Promise.reject(error);
  }
);

// API de autenticação
export const authAPI = {
  // Login
  login: (email, password, senhaEncrypted = null) => {
    const payload = { email };

    // Se tiver senha criptografada, enviar ela; caso contrário, enviar senha em texto (compatibilidade)
    if (senhaEncrypted) {
      payload.senhaEncrypted = senhaEncrypted;
    } else {
      payload.senha = password;
    }

    return api.post('/auth/login', payload);
  },

  // Logout
  logout: (token) => {
    return api.post('/auth/logout', {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Verificar token
  verifyToken: (token) => {
    return api.get('/auth/verify', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Redefinir senha por UUID
  redefinirSenhaPorUuid: (uuid, senha) => {
    return api.post(`/auth/redefinir-senha/${uuid}`, { senha });
  },

  // Buscar usuário por UUID
  buscarUsuarioPorUuid: (uuid) => {
    return api.get(`/auth/buscar-usuario/${uuid}`);
  },

  // Solicitar recuperação de senha
  solicitarRecuperacaoSenha: (email) => {
    return api.post('/auth/solicitar-recuperacao-senha', { email });
  },
};

// API de veículos
export const veiculosAPI = {
  // Listar veículos com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    const url = `/veiculos?${queryParams.toString()}`;
    return api.get(url);
  },

  // Buscar veículo por ID
  buscarPorId: (id) => {
    return api.get(`/veiculos/${id}`);
  },


  // Listar acessórios disponíveis
  listarAcessorios: () => {
    return api.get('/veiculos/acessorios');
  },

  // Criar novo veículo
  criar: (dados) => {
    return api.post('/veiculos', dados);
  },

  // Atualizar veículo
  atualizar: (id, dados) => {
    return api.put(`/veiculos/${id}`, dados);
  },

  // Deletar veículo
  deletar: (id) => {
    return api.delete(`/veiculos/${id}`);
  },

  // Listar clientes para select
  listarClientes: () => {
    return api.get('/veiculos/clientes');
  },

  // Upload de imagens para veículo
  uploadImagens: (veiculoId, formData) => {
    // IMPORTANTE: sobrescrever o header default 'application/json' do axios instance
    // para 'multipart/form-data', senão o multer não recebe os arquivos (req.files = [])
    return api.post(`/veiculos/${veiculoId}/imagens`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// API de usuários
export const usuariosAPI = {
  // Listar usuários com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    return api.get(`/usuarios?${queryParams.toString()}`);
  },

  // Buscar usuário por ID
  buscarPorId: (id) => {
    return api.get(`/usuarios/${id}`);
  },

  // Criar novo usuário
  criar: (dados) => {
    return api.post('/usuarios', dados);
  },

  // Atualizar usuário
  atualizar: (id, dados) => {
    return api.put(`/usuarios/${id}`, dados);
  },

  // Deletar usuário
  deletar: (id) => {
    return api.delete(`/usuarios/${id}`);
  },

  // Limpar senha do usuário
  limparSenha: (id) => {
    return api.put(`/usuarios/${id}/limpar-senha`);
  },

  // Listar permissões disponíveis
  listarPermissoes: () => {
    return api.get('/usuarios/permissoes');
  },
};

// API de parametros do whatsapp
export const parametrosWhatsappAPI = {
  // Listar com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/parametros-whatsapp?${queryParams.toString()}`);
  },

  // Buscar por ID
  buscarPorId: (id) => {
    return api.get(`/parametros-whatsapp/${id}`);
  },

  // Criar novo
  criar: (dados) => {
    return api.post('/parametros-whatsapp', dados);
  },

  // Atualizar
  atualizar: (id, dados) => {
    return api.put(`/parametros-whatsapp/${id}`, dados);
  },

  // Deletar
  deletar: (id) => {
    return api.delete(`/parametros-whatsapp/${id}`);
  },
};

// API de administradores
export const administradoresAPI = {
  // Listar administradores com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/administradores?${queryParams.toString()}`);
  },

  // Buscar administrador por ID/UUID
  buscarPorId: (id) => {
    return api.get(`/administradores/${id}`);
  },

  // Verificar se já existe pelo menos um administrador
  existeAlgum: () => {
    return api.get('/administradores/exists');
  },

  // Criar primeiro administrador (sem autenticação, se não existir nenhum)
  criarPrimeiro: (dados) => {
    return api.post('/administradores/primeiro', dados);
  },

  // Criar novo administrador
  criar: (dados) => {
    return api.post('/administradores', dados);
  },

  // Atualizar administrador
  atualizar: (id, dados) => {
    return api.put(`/administradores/${id}`, dados);
  },

  // Deletar administrador
  deletar: (id) => {
    return api.delete(`/administradores/${id}`);
  },
};

// API de pré-cadastros
export const preCadastrosAPI = {
  // Buscar pré-cadastro por UUID
  buscarPorUuid: (uuid) => {
    return api.get(`/pre-cadastros/${uuid}`);
  },

  // Confirmar pré-cadastro
  confirmar: (uuid, senha) => {
    return api.post(`/pre-cadastros/${uuid}/confirmar`, { senha });
  },
};

// API de contatos
export const contatosAPI = {
  // Listar contatos com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    return api.get(`/contatos?${queryParams.toString()}`);
  },

  // Listar clientes disponíveis para o usuário
  listarClientes: () => {
    return api.get('/contatos/clientes');
  },

  // Importar contatos da Evolution API
  importar: (idCliente = null) => {
    const payload = {};
    if (idCliente) {
      payload.id_cliente = idCliente;
    }
    return api.post('/contatos/importar', payload);
  },

  // Favoritar/desfavoritar contato
  favoritar: (sessionKey, favorito, idCliente = null) => {
    const payload = { favorito };
    if (idCliente) {
      payload.id_cliente = idCliente;
    }
    return api.put(`/contatos/${encodeURIComponent(sessionKey)}/favoritar`, payload);
  },

  // Favoritar/desfavoritar todos os contatos (aplica filtros)
  favoritarTodos: (favorito, filtros = {}) => {
    const payload = {
      favorito,
      ...filtros
    };
    return api.post('/contatos/favoritar-todos', payload);
  },
};


// API de configurações
export const configuracoesAPI = {
  // Listar configurações com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    return api.get(`/configuracoes?${queryParams.toString()}`);
  },

  // Buscar configuração por ID
  buscarPorId: (id) => {
    return api.get(`/configuracoes/${id}`);
  },

  // Criar nova configuração
  criar: (dados) => {
    return api.post('/configuracoes', dados);
  },

  // Atualizar configuração
  atualizar: (id, dados) => {
    return api.put(`/configuracoes/${id}`, dados);
  },

  // Listar clientes disponíveis para filtro
  listarClientes: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    const url = `/configuracoes/clientes${queryString ? `?${queryString}` : ''}`;
    return api.get(url);
  },

  // Deletar configuração
  deletar: (id) => {
    return api.delete(`/configuracoes/${id}`);
  },
};

// API de clientes
export const clientesAPI = {
  // Listar clientes com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    const url = `/clientes?${queryParams.toString()}`;
    return api.get(url);
  },

  // Buscar cliente por ID
  buscarPorId: (id) => {
    const url = `/clientes/${id}`;
    return api.get(url);
  },

  // Resumo das configurações necessárias dos clientes acessíveis
  resumoConfiguracoes: () => {
    return api.get('/clientes/resumo-configuracoes');
  },

  // Estatísticas do dashboard (veículos e clientes)
  estatisticasDashboard: () => {
    return api.get('/clientes/estatisticas-dashboard');
  },

  // Criar novo cliente
  criar: (dados) => {
    return api.post('/clientes', dados);
  },

  // Atualizar cliente
  atualizar: (id, dados) => {
    return api.put(`/clientes/${id}`, dados);
  },

  // Criar cliente na API Asaas
  criarAsaas: (id, dados) => {
    return api.post(`/clientes/${id}/asaas`, dados);
  },

  // Consultar cliente na API Asaas
  consultarAsaas: (idOuUuid) => {
    return api.get(`/clientes/${idOuUuid}/asaas`);
  },

  // Atualizar cliente na API Asaas
  atualizarAsaas: (idOuUuid, dados) => {
    return api.put(`/clientes/${idOuUuid}/asaas`, dados);
  },
};

// API para informações adicionais da revenda (cliente_sobre)
export const clienteSobreAPI = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    const url = `/cliente-sobre${queryString ? `?${queryString}` : ''}`;
    return api.get(url);
  },

  buscarPorId: (id) => {
    return api.get(`/cliente-sobre/${id}`);
  },

  criar: (dados) => {
    return api.post('/cliente-sobre', dados);
  },

  atualizar: (id, dados) => {
    return api.put(`/cliente-sobre/${id}`, dados);
  },

  deletar: (id) => {
    return api.delete(`/cliente-sobre/${id}`);
  },

  listarClientes: () => {
    return api.get('/cliente-sobre/clientes');
  },
};

// API de planos comerciais
export const planosAPI = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return api.get(`/planos${queryString ? `?${queryString}` : ''}`);
  },

  buscarPorId: (id) => {
    return api.get(`/planos/${id}`);
  },

  criar: (dados) => {
    return api.post('/planos', dados);
  },

  atualizar: (id, dados) => {
    return api.put(`/planos/${id}`, dados);
  },

  atualizarStatus: (id, ativo) => {
    return api.patch(`/planos/${id}/status`, { ativo });
  },

  listarRecursos: (id) => {
    return api.get(`/planos/${id}/recursos`);
  },

  sincronizarRecursos: (id, recursoIds) => {
    return api.put(`/planos/${id}/recursos`, { recurso_ids: recursoIds });
  },

  listarPlanosUpgrade: (clienteId, clienteUuid) => {
    if (clienteUuid) {
      return api.get(`/planos/upgrade/disponiveis?cliente_uuid=${clienteUuid}`);
    }
    return api.get(`/planos/upgrade/disponiveis?cliente_id=${clienteId}`);
  },

  realizarUpgrade: (clienteId, planoId, cartaoId) => {
    return api.post('/planos/upgrade', { cliente_id: clienteId, plano_id: planoId, cartao_id: cartaoId });
  },
};

// API de recursos do catálogo
export const recursosAPI = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return api.get(`/recursos${queryString ? `?${queryString}` : ''}`);
  },

  buscarPorId: (id) => {
    return api.get(`/recursos/${id}`);
  },

  criar: (dados) => {
    return api.post('/recursos', dados);
  },

  atualizar: (id, dados) => {
    return api.put(`/recursos/${id}`, dados);
  },

  listarPlanos: (id) => {
    return api.get(`/recursos/${id}/planos`);
  },
};

// API de contratos e créditos
export const contratosAPI = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return api.get(`/contratos${queryString ? `?${queryString}` : ''}`);
  },

  buscarPorId: (id) => {
    return api.get(`/contratos/${id}`);
  },

  criar: (dados) => {
    return api.post('/contratos', dados);
  },

  atualizar: (id, dados) => {
    return api.put(`/contratos/${id}`, dados);
  },

  atualizarStatus: (id, status) => {
    return api.patch(`/contratos/${id}/status`, { status });
  },

  listarRecursos: (id) => {
    return api.get(`/contratos/${id}/recursos`);
  },

  sincronizarRecursos: (id, recursoIds) => {
    return api.put(`/contratos/${id}/recursos`, { recurso_ids: recursoIds });
  },

  listarCiclos: (id, params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return api.get(`/contratos/${id}/ciclos${queryString ? `?${queryString}` : ''}`);
  },

  criarCiclo: (id, dados) => {
    return api.post(`/contratos/${id}/ciclos`, dados);
  },

  buscarCiclo: (id, cicloId) => {
    return api.get(`/contratos/${id}/ciclos/${cicloId}`);
  },

  adicionarExtensao: (id, cicloId, dados) => {
    return api.post(`/contratos/${id}/ciclos/${cicloId}/extensoes`, dados);
  },

  solicitarExtensao: (id, dados) => {
    return api.post(`/contratos/${id}/extensoes`, dados);
  },
  confirmarPagamentoExtensao: (cobrancaId, dados = {}) => {
    return api.post(`/contratos/extensoes/${cobrancaId}/confirmar-pagamento`, dados);
  },
  excluirCobrancaExtensao: (cobrancaId) => {
    return api.delete(`/contratos/extensoes/${cobrancaId}`);
  },
  confirmarPagamentoCiclo: (cobrancaId, dados = {}) => {
    return api.post(`/contratos/ciclos/${cobrancaId}/confirmar-pagamento`, dados);
  },
  excluirCobrancaCiclo: (cobrancaId) => {
    return api.delete(`/contratos/ciclos/${cobrancaId}`);
  },

  gerarCobrancaCiclo: (contratoId, cicloId) => {
    return api.post(`/contratos/${contratoId}/ciclos/${cicloId}/gerar-cobranca`);
  },

  verificarCobrancaCiclo: (contratoId, cicloId) => {
    return api.get(`/contratos/${contratoId}/ciclos/${cicloId}/cobranca`);
  },

  listarExtensoes: (id, cicloId) => {
    return api.get(`/contratos/${id}/ciclos/${cicloId}/extensoes`);
  },

  registrarConsumo: (id, cicloId, dados) => {
    return api.post(`/contratos/${id}/ciclos/${cicloId}/consumos`, dados);
  },

  listarConsumos: (id, cicloId) => {
    return api.get(`/contratos/${id}/ciclos/${cicloId}/consumos`);
  },

  dashboardGeral: () => {
    return api.get('/dashboard/contratos/geral');
  },

  dashboardCliente: (clienteId) => {
    return api.get(`/dashboard/contratos/clientes/${clienteId}`);
  },

  dashboardMeusContratos: (clienteId = null) => {
    const params = clienteId ? `?cliente_id=${clienteId}` : '';
    return api.get(`/dashboard/contratos/meus-contratos${params}`);
  },

  buscarContratoAtivoPorCliente: (clienteId) => {
    return api.get(`/contratos/cliente/${clienteId}/ativo`);
  },
  buscarConsumoPorCliente: (clienteId, periodo = 'ciclo_atual') => {
    return api.get(`/contratos/cliente/${clienteId}/consumo?periodo=${periodo}`);
  },
  calcularMultaRescisao: (contratoId) => {
    return api.get(`/contratos/${contratoId}/calcular-multa-rescisao`);
  },
  recindirContrato: (contratoId) => {
    return api.post(`/contratos/${contratoId}/recindir`);
  },
};

// API de Keys (API Keys para autenticação externa)
export const keysApisAPI = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return api.get(`/keys-apis${queryString ? `?${queryString}` : ''}`);
  },

  buscarPorId: (id) => {
    return api.get(`/keys-apis/${id}`);
  },

  criar: (dados) => {
    return api.post('/keys-apis', dados);
  },

  atualizar: (id, dados) => {
    return api.put(`/keys-apis/${id}`, dados);
  },

  deletar: (id) => {
    return api.delete(`/keys-apis/${id}`);
  },
};

// API de parâmetros de cobrança
export const parametrosCobrancaAPI = {
  // Listar parâmetros de cobrança
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    return api.get(`/parametros-cobranca?${queryParams.toString()}`);
  },

  // Buscar parâmetros por cliente
  buscarPorCliente: (clienteId) => {
    return api.get(`/parametros-cobranca/cliente/${clienteId}`);
  },

  // Criar ou atualizar parâmetros de cobrança
  salvar: (clienteId, dados) => {
    return api.post(`/parametros-cobranca/cliente/${clienteId}`, dados);
  },

  // Deletar parâmetros de cobrança
  deletar: (clienteId) => {
    return api.delete(`/parametros-cobranca/cliente/${clienteId}`);
  },
};

// API de cartões de crédito
export const cartoesCreditoAPI = {
  // Listar cartões de crédito
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    return api.get(`/cartoes-credito?${queryParams.toString()}`);
  },

  // Buscar cartões por cliente
  buscarPorCliente: (clienteId, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    return api.get(`/cartoes-credito/cliente/${clienteId}${queryString ? `?${queryString}` : ''}`);
  },

  // Listar cartões por cliente (alias para compatibilidade)
  listarPorCliente: (clienteId) => {
    return api.get(`/cartoes-credito/cliente/${clienteId}`);
  },

  // Buscar cartão por ID
  buscarPorId: (id) => {
    return api.get(`/cartoes-credito/${id}`);
  },

  // Criar cartão de crédito
  criar: (dados) => {
    return api.post('/cartoes-credito', dados);
  },

  // Atualizar cartão de crédito
  atualizar: (id, dados) => {
    return api.put(`/cartoes-credito/${id}`, dados);
  },

  // Deletar cartão de crédito
  deletar: (id) => {
    return api.delete(`/cartoes-credito/${id}`);
  },
};

export const cartaoPublicoAPI = {
  // Gerar token para cadastro público de cartão
  gerarToken: (clienteUuid) => {
    return api.post('/cartao-publico/gerar-token', { cliente_uuid: clienteUuid });
  },

  // Validar token
  validarToken: (token) => {
    return api.get(`/cartao-publico/validar-token/${token}`);
  },

  // Salvar cartão via token público
  salvarCartao: (token, dados) => {
    return api.post(`/cartao-publico/salvar-cartao/${token}`, dados);
  },
};

export const meusCartoesAPI = {
  // Listar cartões do cliente logado
  listar: () => {
    return api.get('/meus-cartoes');
  },

  // Buscar um cartão específico
  buscar: (id) => {
    return api.get(`/meus-cartoes/${id}`);
  },

  // Criar novo cartão
  criar: (dados) => {
    return api.post('/meus-cartoes', dados);
  },

  // Atualizar cartão
  atualizar: (id, dados) => {
    return api.put(`/meus-cartoes/${id}`, dados);
  },

  // Deletar cartão
  deletar: (id) => {
    return api.delete(`/meus-cartoes/${id}`);
  },
};

export const ibgeAPI = {
  listarEstados: () => {
    return axios.get('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
  },
  listarMunicipios: (siglaEstado) => {
    return axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${siglaEstado}/municipios`);
  }
};

// API de parâmetros de IA para clientes
export const parametrosIAAPI = {
  // Listar parâmetros com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/parametros-ia?${queryParams.toString()}`);
  },

  // Buscar parâmetro por ID
  buscarPorId: (id) => {
    return api.get(`/parametros-ia/${id}`);
  },

  // Criar novo parâmetro
  criar: (dados) => {
    return api.post('/parametros-ia', dados);
  },

  // Atualizar parâmetro
  atualizar: (id, dados) => {
    return api.put(`/parametros-ia/${id}`, dados);
  },

  // Deletar parâmetro
  deletar: (id) => {
    return api.delete(`/parametros-ia/${id}`);
  },

  // Listar clientes disponíveis
  listarClientes: () => {
    return api.get('/parametros-ia/clientes/lista');
  },

  // Listar tons de voz disponíveis
  listarTones: () => {
    return api.get('/parametros-ia/tones');
  },

  // Listar modelos de IA disponíveis
  listarModelos: () => {
    return api.get('/parametros-ia/modelos');
  },

  // Gerar system prompt baseado no tom
  gerarPrompt: (tone, clienteId = null) => {
    const params = new URLSearchParams();
    if (tone) params.append('tone', tone);
    if (clienteId) params.append('cliente_id', clienteId);
    return api.get(`/parametros-ia/gerar-prompt?${params.toString()}`);
  },

  // Ressincronizar com N8N
  sincronizar: (id) => {
    return api.post(`/parametros-ia/${id}/sincronizar`);
  },
};

// API de configuração de IA para clientes (gestor)
export const configuracaoIAAPI = {
  // Listar configurações com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/configuracao-ia?${queryParams.toString()}`);
  },

  // Buscar configuração por ID
  buscarPorId: (id) => {
    return api.get(`/configuracao-ia/${id}`);
  },

  // Atualizar configuração
  atualizar: (id, dados) => {
    return api.put(`/configuracao-ia/${id}`, dados);
  },

  // Listar tons de voz disponíveis
  listarTones: () => {
    return api.get('/configuracao-ia/tones');
  },

  // Gerar system prompt baseado no tom
  gerarPrompt: (tone, clienteId = null) => {
    const params = new URLSearchParams();
    if (tone) params.append('tone', tone);
    if (clienteId) params.append('cliente_id', clienteId);
    return api.get(`/configuracao-ia/gerar-prompt?${params.toString()}`);
  },
};

// API genérica para outras funcionalidades
export const apiService = {
  // GET
  get: (url, config = {}) => {
    return api.get(url, config);
  },

  // POST
  post: (url, data, config = {}) => {
    return api.post(url, data, config);
  },

  // PUT
  put: (url, data, config = {}) => {
    return api.put(url, data, config);
  },

  // PATCH
  patch: (url, data, config = {}) => {
    return api.patch(url, data, config);
  },

  // DELETE
  delete: (url, config = {}) => {
    return api.delete(url, config);
  },
};

// API de funis de prospecção
export const funisProspeccaoAPI = {
  // Listar funis com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/funis-prospeccao?${queryParams.toString()}`);
  },

  // Listar todos os funis (para selects)
  listarTodos: () => {
    return api.get('/funis-prospeccao/todos');
  },

  // Buscar funil por ID
  buscarPorId: (id) => {
    return api.get(`/funis-prospeccao/${id}`);
  },

  // Criar novo funil
  criar: (dados) => {
    return api.post('/funis-prospeccao', dados);
  },

  // Atualizar funil
  atualizar: (id, dados) => {
    return api.put(`/funis-prospeccao/${id}`, dados);
  },

  // Deletar funil
  deletar: (id) => {
    return api.delete(`/funis-prospeccao/${id}`);
  },
};

// API de etapas
export const etapasAPI = {
  // Listar etapas de um funil
  listarPorFunil: (funilId) => {
    return api.get(`/etapas/funil/${funilId}`);
  },

  // Buscar etapa por ID
  buscarPorId: (id) => {
    return api.get(`/etapas/${id}`);
  },

  // Criar nova etapa
  criar: (dados) => {
    return api.post('/etapas', dados);
  },

  // Atualizar etapa
  atualizar: (id, dados) => {
    return api.put(`/etapas/${id}`, dados);
  },

  // Deletar etapa
  deletar: (id) => {
    return api.delete(`/etapas/${id}`);
  },
};

// API de mensagens por etapa
export const mensagensEtapaAPI = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });

    return api.get(`/mensagens-etapa?${queryParams.toString()}`);
  },

  buscarPorId: (id) => {
    return api.get(`/mensagens-etapa/${id}`);
  },

  criar: (dados) => {
    return api.post('/mensagens-etapa', dados);
  },

  atualizar: (id, dados) => {
    return api.put(`/mensagens-etapa/${id}`, dados);
  },

  deletar: (id) => {
    return api.delete(`/mensagens-etapa/${id}`);
  },
};

// API de origens de lead
export const origensLeadAPI = {
  listar: () => {
    return api.get('/origens-lead');
  },
};

// API de leads
export const leadsAPI = {
  // Listar leads com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/leads?${queryParams.toString()}`);
  },

  // Buscar lead por ID
  buscarPorId: (id) => {
    return api.get(`/leads/${id}`);
  },

  // Criar novo lead
  criar: (dados) => {
    return api.post('/leads', dados);
  },

  // Atualizar lead
  atualizar: (id, dados) => {
    return api.put(`/leads/${id}`, dados);
  },

  // Deletar lead
  deletar: (id) => {
    return api.delete(`/leads/${id}`);
  },

  // Listar funis disponíveis
  listarFunis: () => {
    return api.get('/leads/funis');
  },

  // Listar etapas de um funil
  listarEtapas: (funilId) => {
    return api.get(`/leads/etapas/${funilId}`);
  },

  // Listar origens de lead
  listarOrigens: () => {
    return api.get('/leads/origens');
  },

  // Contagem para tela de confirmar envio (total leads e total emails por IDs) — POST evita limite de URL
  contagemEnvio: (ids) => {
    const arr = Array.isArray(ids) ? ids : (typeof ids === 'string' ? ids.split(',').map((id) => id.trim()).filter(Boolean) : []);
    return api.post('/leads/contagem-envio', { ids: arr });
  },

  // Importar leads a partir de arquivo xlsx
  importar: (arquivo) => {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    return api.post('/leads/importar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Atualizar etapa de múltiplos leads em lote
  atualizarEtapaLote: (updates) => {
    return api.post('/leads/atualizar-etapa-lote', { updates });
  },

  // Pesquisar revendas na internet (municipio_ibge opcional: evita chamada ao IBGE no backend)
  // apiSource: 'serpapi' | 'minhareceita'
  pesquisarInternet: (cidade, estado, municipioIbge = null, apiSource = 'serpapi') => {
    const params = new URLSearchParams({ cidade, estado, api: apiSource });
    if (municipioIbge != null && municipioIbge !== '') params.set('municipio_ibge', String(municipioIbge));
    return api.get(`/leads/pesquisar-internet?${params.toString()}`, { timeout: 60000 });
  },

  // Importar leads da pesquisa na internet (lote, mesma regra do xlsx no backend)
  importarInternet: (leads) => {
    return api.post('/leads/importar-internet', { leads });
  },
};

// API de campanhas de WhatsApp
export const campanhasAPI = {
  // Listar campanhas com filtros e paginação
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    return api.get(`/campanhas?${queryParams.toString()}`);
  },

  // Buscar campanha por ID
  buscarPorId: (id) => {
    return api.get(`/campanhas/${id}`);
  },

  // Criar nova campanha
  criar: (formData) => {
    return api.post('/campanhas', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Atualizar campanha
  atualizar: (id, formData) => {
    return api.put(`/campanhas/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Deletar campanha
  deletar: (id) => {
    return api.delete(`/campanhas/${id}`);
  },

  // Listar clientes disponíveis para campanhas
  listarClientes: () => {
    return api.get('/campanhas/clientes');
  },

  // Listar contatos da campanha
  listarContatos: (id) => {
    return api.get(`/campanhas/${id}/contatos`);
  },
};

// API de status de prospecção
export const statusProspeccaoAPI = {
  listar: () => api.get('/status-prospeccao'),
};

export default api;
